// Kişisel istatistik panosunun veri hazırlığı. Sayılar sunucudan
// (getMyStatistics: boş gün 0, fazlası sayılmaz, ay dökümü o günlerin
// hedefleriyle); burada yalnız grafiklere uygun biçime getiriliyor.

import { goalKey } from "../app/goals/goalRules";
import { appliesOn } from "./dayGoals";

const TR_MONTHS_SHORT = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
const TR_MONTHS = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık",
];

const pad = (n) => String(n).padStart(2, "0");

/** "YYYY-MM-DD" gününe gün ekler (yerel takvim, saat diliminden bağımsız). */
export function addDays(localDate, days) {
  const [y, m, d] = localDate.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, d + days));
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

/** "YYYY-MM" → "Eylül 2026" (uzun) ya da "Eyl" (kısa). */
export function monthLabel(monthKey, short = false) {
  const [y, m] = String(monthKey || "").split("-").map(Number);
  if (!y || !m) return "";
  return short ? TR_MONTHS_SHORT[m - 1] : `${TR_MONTHS[m - 1]} ${y}`;
}

/** Bir ay önceki ayın anahtarı. */
export function previousMonthKey(monthKey) {
  const [y, m] = monthKey.split("-").map(Number);
  return m === 1 ? `${y - 1}-12` : `${y}-${pad(m - 1)}`;
}

/**
 * Son 30 gün serisi (bugün dahil): iki ayın kayıtlarından günlük tamamlama.
 * Girilmeyen (hasEntry false) ya da kaydı olmayan gün 0; bugün girilmediyse
 * nokta yok (gün bitmedi). Takip başlamadan önceki günler seriye alınmaz
 * (grafik boş başlangıçla aşağı çekilmesin). Oran gün puanı (dayScore),
 * eski sunucuda saklanan ortalama.
 */
export function last30Series(monthLogs, today) {
  const byDate = new Map();
  for (const day of monthLogs || []) {
    if (day?.date) byDate.set(day.date, day);
  }
  const start = addDays(today, -29);
  const firstEntered = [...byDate.values()]
    .filter((d) => d.hasEntry || d.log?.dayTotals?.hasEntry)
    .map((d) => d.date)
    .sort()[0];
  const series = [];
  for (let date = start; date <= today; date = addDays(date, 1)) {
    if (firstEntered && date < firstEntered) continue;
    const day = byDate.get(date);
    const entered = Boolean(day && (day.hasEntry || day.log?.dayTotals?.hasEntry));
    if (date === today && !entered) continue;
    const totals = day?.dayTotals || day?.log?.dayTotals || {};
    const score = Number(day?.dayScore ?? totals.avgCompletion) || 0;
    series.push({
      date,
      label: `${Number(date.slice(8))} ${TR_MONTHS_SHORT[Number(date.slice(5, 7)) - 1]}`,
      rate: entered ? Math.round(Math.min(100, Math.max(0, score))) : 0,
      perfect: Boolean(entered && totals.isPerfectDay),
    });
  }
  return series;
}

/** Aylık gidiş: eskiden yeniye, grafik için kısa ay adıyla. */
export function monthlySeries(monthlyStats) {
  return [...(monthlyStats || [])]
    .sort((a, b) => a.monthKey.localeCompare(b.monthKey))
    .map((m) => ({
      monthKey: m.monthKey,
      label: monthLabel(m.monthKey, true),
      rate: Math.round(Number(m.avgCompletion) || 0),
      perfectDays: m.perfectDays || 0,
      totalDays: m.totalDays || 0,
    }));
}

/** Seçili ayın hedef dökümü, en yüksek tamamlama başta. */
export function goalBreakdown(monthlyStats, monthKey) {
  const month = (monthlyStats || []).find((m) => m.monthKey === monthKey);
  return [...(month?.goalDetails || [])]
    .map((g) => ({
      key: `${g.label}::${g.activity || ""}`,
      label: g.label,
      activity: g.activity,
      unit: g.unit,
      rate: Math.round(Number(g.completionRate) || 0),
      total: g.totalValue || 0,
      average: g.avgValue || 0,
      days: g.days || 0,
    }))
    .sort((a, b) => b.rate - a.rate || a.label.localeCompare(b.label, "tr"));
}

/**
 * Ayın hedef dökümü doğrudan o ayın günlük kayıtlarından (takvimle aynı
 * kaynak): bir günün hedefleri, girilen satırlar ile o günün hedef
 * görüntüsünde (goalDetailSnapshot) o gün geçerli olanlar. Satırı olmayan
 * geçerli hedef o gün 0 sayılır; oran gün gün kırpılıp ortalanır (fazla
 * yapmak boş günü telafi etmez). Bugün yalnız girildiyse sayılır. Kaydı hiç
 * olmayan gün bilinemediği için sayılmaz.
 */
export function goalsFromMonthLogs(dailyLogs, today) {
  const byKey = new Map();
  const itemOf = (source) => {
    const key = goalKey(source.label, source.activity);
    if (!byKey.has(key)) {
      byKey.set(key, {
        key,
        label: source.label,
        activity: source.activity,
        unit: source.unit,
        sum: 0,
        counted: 0,
        total: 0,
        days: 0,
      });
    }
    const item = byKey.get(key);
    if (!item.unit && source.unit) item.unit = source.unit;
    return item;
  };

  for (const day of dailyLogs || []) {
    const log = day?.log;
    if (!day?.date || !log || day.date > today) continue;
    const entered = Boolean(day.hasEntry ?? log.dayTotals?.hasEntry);
    if (day.date === today && !entered) continue;
    const seen = new Set();
    for (const row of log.dailyLogs || []) {
      if (!row?.label) continue;
      const item = itemOf(row);
      const target = Number(row.goalValue) || 0;
      const value = Number(row.value) || 0;
      item.sum += target > 0 ? Math.min(100, (value / target) * 100) : 0;
      item.counted += 1;
      if (entered) {
        item.total += value;
        if (value > 0) item.days += 1;
      }
      seen.add(item.key);
    }
    const snapshot = Array.isArray(day.goalDetailSnapshot)
      ? day.goalDetailSnapshot
      : Array.isArray(log.goalDetailSnapshot)
        ? log.goalDetailSnapshot
        : [];
    for (const goal of snapshot) {
      if (!goal?.label || !appliesOn(goal.weekDays, day.date)) continue;
      const item = itemOf(goal);
      if (seen.has(item.key)) continue;
      item.counted += 1;
      seen.add(item.key);
    }
  }

  return [...byKey.values()]
    .filter((g) => g.counted > 0)
    .map((g) => ({
      key: g.key,
      label: g.label,
      activity: g.activity,
      unit: g.unit,
      rate: Math.round(g.sum / g.counted),
      total: g.total,
      average: g.days > 0 ? Math.round(g.total / g.days) : 0,
      days: g.days,
    }));
}

/**
 * Ekrandaki döküm: günlük kayıtlardan çıkan bütün hedefler; sunucu aynı
 * hedef için hesap yaptıysa (kaydı olmayan günleri de sayar) onun sayıları.
 * Sunucuda olup kayıtlarda görünmeyen hedef de eklenir.
 */
export function monthGoalRows({ monthlyStats, monthKey, dailyLogs, today }) {
  const server = new Map(goalBreakdown(monthlyStats, monthKey).map((g) => [goalKey(g.label, g.activity), g]));
  const rows = goalsFromMonthLogs(dailyLogs, today).map((g) => {
    const s = server.get(g.key);
    return s ? { ...s, key: g.key, unit: s.unit || g.unit } : g;
  });
  const have = new Set(rows.map((r) => r.key));
  for (const [key, s] of server) if (!have.has(key)) rows.push({ ...s, key });
  return rows.sort((a, b) => b.rate - a.rate || a.label.localeCompare(b.label, "tr"));
}
